import { useAuth } from "@/auth/AuthContext";
import { useTheme } from "@/theme/ThemeProvider";
import { AppButton } from "@/ui/components/AppButton";
import { HIT_TARGET_MIN } from "@/ui/components/listRowA11y";
import { useFinderMode } from "@/ui/finder/FinderModeContext";
import { FINDER_GATE_CRASH, FINDER_GATE_END, FINDER_LOCK_SIGN_OUT } from "@/ui/finder/finderMode";
import { bindWebOverlayTrap, webTitleProps } from "@/ui/finder/finderOverlayFocus";
import { useEffect, useRef } from "react";
import { Platform, Text, View } from "react-native";

const actionStyle = { alignSelf: "stretch" as const, minHeight: HIT_TARGET_MIN };

/**
 * Notfall-Sperre. Gibt die App nicht frei, bis „Finder-Modus beenden“
 * dieselbe Rückgabe auslöst wie „Ich bin die Pflegekraft – weiter“.
 */
export function FinderGateFallback({ resetError }: { resetError: () => void }) {
  const { theme } = useTheme();
  const { signOut } = useAuth();
  const { completeHandover } = useFinderMode();
  const rootRef = useRef<View>(null);
  const titleRef = useRef<Text>(null);
  const webDialog =
    Platform.OS === "web"
      ? {
          role: "dialog" as const,
          "aria-modal": true,
        }
      : {};

  useEffect(() => {
    if (Platform.OS !== "web") return;
    titleRef.current?.focus?.();
  }, []);

  useEffect(() => {
    const root = rootRef.current as unknown as HTMLElement | null;
    return bindWebOverlayTrap(root, () => {
      titleRef.current?.focus?.();
    });
  }, []);

  useEffect(() => {
    if (Platform.OS !== "web") return;
    const hist = globalThis.history;
    const win = globalThis.window;
    if (!hist?.pushState || !win?.addEventListener) return;
    hist.pushState({ finderGate: true }, "");
    const onPop = () => {
      hist.pushState({ finderGate: true }, "");
    };
    win.addEventListener("popstate", onPop);
    return () => win.removeEventListener("popstate", onPop);
  }, []);

  return (
    <View
      ref={rootRef}
      accessibilityViewIsModal
      accessibilityLabel="Finder-Sperre"
      {...webDialog}
      style={{
        pointerEvents: "auto",
        position: Platform.OS === "web" ? ("fixed" as "absolute") : "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 10000,
        elevation: 10000,
        backgroundColor: theme.color.bg,
        justifyContent: "center",
        padding: theme.space.lg,
      }}
    >
      <View
        style={{
          maxWidth: theme.layout.readable,
          width: "100%",
          alignSelf: "center",
          gap: theme.space.md,
        }}
      >
        <Text
          ref={titleRef}
          accessibilityRole="header"
          {...webTitleProps}
          style={{
            color: theme.color.text,
            fontSize: theme.fontSize.title,
            fontWeight: "700",
          }}
        >
          {FINDER_GATE_CRASH}
        </Text>
        <AppButton
          style={actionStyle}
          onPress={() => {
            completeHandover();
            resetError();
          }}
        >
          {FINDER_GATE_END}
        </AppButton>
        <AppButton
          variant="outline"
          style={actionStyle}
          onPress={() => {
            void Promise.resolve(signOut()).catch((error: unknown) => {
              console.error(
                "Finder-Sperre: Abmelden fehlgeschlagen",
                error instanceof Error ? error.message : error,
              );
            });
          }}
        >
          {FINDER_LOCK_SIGN_OUT}
        </AppButton>
      </View>
    </View>
  );
}
